import { computed } from "vue";
import useCreateComponentConfig, { EStates, EVariants } from "../../../composables/useCreateComponentConfig.js";
import focus from "../../constants/focus.js";
import { SIZES } from "@/ui/ts/types/Sizes";
export var ESizingMap = /* @__PURE__ */ ((ESizingMap2) => {
  ESizingMap2["SMALL"] = "size-sm";
  ESizingMap2["MEDIUM"] = "size-base";
  return ESizingMap2;
})(ESizingMap || {});
var ECheckboxElements = /* @__PURE__ */ ((ECheckboxElements2) => {
  ECheckboxElements2["ROOT"] = "ROOT";
  ECheckboxElements2["INDICATOR"] = "INDICATOR";
  ECheckboxElements2["LABEL"] = "LABEL";
  ECheckboxElements2["HELPER"] = "HELPER";
  return ECheckboxElements2;
})(ECheckboxElements || {});
export default function useCheckboxConfig(props) {
  const { rootClasses } = useRootConfig(props);
  const { indicatorClasses } = useIndicatorConfig(props);
  const { labelClasses } = useLabelConfig(props);
  return {
    rootClasses,
    indicatorClasses,
    labelClasses
  };
}
function useRootConfig(props) {
  const { setBase, addBaseState, addSize, setDefault, getClasses, overwriteBaseState } = useCreateComponentConfig();
  setBase(
    "ROOT" /* ROOT */,
    /* tw */
    `shrink-0 rounded-control border border-2 transition-colors delay-0 duration-200 ease-linear data-[state=checked]:bg-brand-accent-invert data-[state=unchecked]:bg-neutral-primary data-[state=checked]:border-brand-accent-invert border-neutral-default`
  );
  addBaseState("ROOT" /* ROOT */, EStates.HOVER, `enabled:hover:ring-4 enabled:hover:ring-comp-ghost-pressed`);
  addBaseState("ROOT" /* ROOT */, EStates.FOCUSED, `${focus}`);
  addBaseState(
    "ROOT" /* ROOT */,
    EStates.DISABLED,
    `aria-disabled:bg-disabled aria-disabled:border-disabled data-[state=checked]:aria-disabled:bg-disabled data-[state=checked]:aria-disabled:border-disabled data-[state=checked]:hover:aria-disabled:bg-disabled data-[state=unchecked]:bg-disabled pointer-events-none select-none`
  );
  addBaseState(
    "ROOT" /* ROOT */,
    EStates.ERROR,
    /* tw */
    `border-validation-danger data-[state=checked]:border-validation-danger bg-neutral-primary data-[state=checked]:bg-validation-danger-bold data-[state=checked]:focus:bg-validation-danger data-[state=checked]:hover:bg-validation-danger indeterminate:bg-validation-danger indeterminate:hover:bg-validation-danger checkbox-error hover:text-white data-[state=checked]:text-white`,
    {
      func: (elementName, _, __) => {
        overwriteBaseState(
          elementName,
          EStates.HOVER,
          /* tw */
          `enabled:hover:ring-validation-danger-light enabled:hover:ring-4`
        );
      },
      condition: props.state === EStates.ERROR
    }
  );
  addBaseState(
    "ROOT" /* ROOT */,
    EStates.INDETERMINATE,
    `data-[state=checked]:bg-brand-accent-invert data-[state=checked]:border-brand-accent-invert`
  );
  addSize(
    "ROOT" /* ROOT */,
    SIZES.XSMALL,
    /* tw */
    "size-sm" /* SMALL */
  );
  addSize(
    "ROOT" /* ROOT */,
    SIZES.SMALL,
    /* tw */
    "size-sm" /* SMALL */
  );
  addSize(
    "ROOT" /* ROOT */,
    SIZES.MEDIUM,
    /* tw */
    "size-base" /* MEDIUM */
  );
  addSize(
    "ROOT" /* ROOT */,
    SIZES.LARGE,
    /* tw */
    "size-base" /* MEDIUM */
  );
  setDefault("ROOT" /* ROOT */, EVariants.DEFAULT, props.size);
  const rootClasses = computed(() => {
    return getClasses(
      "ROOT" /* ROOT */,
      props.variant || void 0,
      props.size,
      props.state || EStates.DEFAULT,
      props.disabled
    );
  });
  return {
    rootClasses: rootClasses.value
  };
}
function useIndicatorConfig(props) {
  const { setBase, addBaseState, getClasses, overwriteBaseState } = useCreateComponentConfig();
  setBase(
    "INDICATOR" /* INDICATOR */,
    /* tw */
    `flex size-full items-center justify-center text-brand-accent`
  );
  addBaseState("INDICATOR" /* INDICATOR */, EStates.DISABLED, ``, {
    func: (elementName, _, __) => {
      overwriteBaseState(
        elementName,
        EStates.DEFAULT,
        /* tw */
        `data-[state=checked]:text-disabled data-[state=checked]:aria-disabled:text-disabled`
      );
    },
    condition: props.disabled ?? false
  });
  addBaseState("INDICATOR" /* INDICATOR */, EStates.ERROR, `data-[state=checked]:text-high-contrast-invert`);
  const indicatorClasses = computed(
    () => getClasses("INDICATOR" /* INDICATOR */, void 0, props.size, props.state || EStates.DEFAULT)
  );
  return {
    indicatorClasses: indicatorClasses.value
  };
}
function useLabelConfig(props) {
  const { setBase, addBaseState, getClasses, addSize } = useCreateComponentConfig();
  setBase(
    "LABEL" /* LABEL */,
    /* tw */
    `text-comp-high-contrast items-start inline-flex flex-wrap gap-x-2`
  );
  addBaseState("LABEL" /* LABEL */, EStates.DISABLED, `text-disabled`);
  addSize(
    "LABEL" /* LABEL */,
    SIZES.XSMALL,
    /* tw */
    props.fontVariant === "paragraph" ? `paragraph-xs` : `label-xs`
  );
  addSize(
    "LABEL" /* LABEL */,
    SIZES.SMALL,
    /* tw */
    props.fontVariant === "paragraph" ? `paragraph-sm` : `label-sm`
  );
  addSize(
    "LABEL" /* LABEL */,
    SIZES.MEDIUM,
    /* tw */
    props.fontVariant === "paragraph" ? `paragraph-md` : `label-md`
  );
  addSize(
    "LABEL" /* LABEL */,
    SIZES.LARGE,
    /* tw */
    props.fontVariant === "paragraph" ? `paragraph-lg` : `label-lg`
  );
  const labelClasses = computed(
    () => getClasses("LABEL" /* LABEL */, void 0, props.size, props.state || EStates.DEFAULT, props.disabled)
  );
  return {
    labelClasses: labelClasses.value
  };
}
